import React, { useEffect, useMemo, useRef, useState } from "react";
import DynamicChart from '../components/charts/dynamicChart';
import SummaryTable from '../components/sections/summaryTable';
import DealsSummaryTable from '../components/sections/thd/dealSummaryTable';
import FiltersSection from '../components/sections/filters';
import MainCards from '../components/sections/mainCards';
import ThdMainCards from '../components/sections/thd/mainCards';
import { useDispatch, useSelector } from "react-redux";
import { changeDimension, changeFilter, getDealsData} from '../stateManagement/actions/report';
import Loader from '../components/layout/loader';
import { chartPreparation } from "../services/dataHandling";
import { prepareChartRows } from "../services/dealsTableData";
import { PARTITION_MAIN, PARTITION_THD, ADMIN_ACCESS_MASK} from "../utils/constants";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const DIMENSIONS_MAIN = [
    { label: 'No dimension', value: '', key: '0' },
    { label: 'Deal name', value: 'dealName', key: '1' },
    { label: 'Platform', value: 'provider', key: '2' },
    { label: 'Publisher', value: 'publisher', key: '3' },
    { label: 'Website', value: 'website', key: '4' },
];

const DIMENSIONS_THD = [
    { label: 'Deal name', value: 'dealName', key: '1' },
    { label: 'Platform', value: 'provider', key: '2' },
    { label: 'Publisher', value: 'publisher', key: '3' },
    { label: 'Website', value: 'website', key: '4' },
    { label: 'Advertiser', value: 'orderAdvertiser', key: '5' }
];

const DealsPage = () => {
    const dispatch = useDispatch();

    const [fullReport, setFullReport] = useState([]);
    const [dataReloaded, setDataReloaded] = useState(false);
    const [selectedCard, setSelectedCard] = useState('revenue');

    /**
     * Retinem daca pagina curenta a fost deja initializata.
     * Astfel resetam dimension/filterBy doar la prima intrare in pagina,
     * nu si cand userul modifica filtrele manual.
     */
    const initializedPageRef = useRef(null);

    const { user } = useAuth();

    const reportData = useSelector((state) => state.reportReducer);
    const {loading, period, dimension, filterBy, publisherView} = reportData;

    const location = useLocation();

    const isThdPage = location.pathname.includes(PARTITION_THD);
    const PARTITION = isThdPage ? PARTITION_THD : PARTITION_MAIN;
    const DIMENSIONS = isThdPage ? DIMENSIONS_THD : DIMENSIONS_MAIN;
    const SelectedMainCards = isThdPage ? ThdMainCards : MainCards;

    const DEFAULT_DIMENSION = 'dealName';

    const GREETINGS_FOR =
        publisherView === ADMIN_ACCESS_MASK
            ? user && user.subAccount
                ? 'ThematicDigital'
                : 'Gemius'
            : publisherView;

    /**
     * Reset + fetch controlat.
     * - daca este nevoie de reset, facem reset si iesim din efect
     * - request-ul pleaca abia dupa ce Redux are valorile corecte
     */
    useEffect(() => {
        const pageKey = `${PARTITION}-deals-${DEFAULT_DIMENSION}`;
        const isFirstRunForPage = initializedPageRef.current !== pageKey;

        const dimensionIsAllowed = DIMENSIONS.some((item) => item.value === dimension);

        const needsDimensionReset = !dimensionIsAllowed || (isFirstRunForPage && dimension !== DEFAULT_DIMENSION);

        const needsFilterReset = isFirstRunForPage && filterBy !== '';

        if (needsFilterReset) {
            dispatch(changeFilter(''));
        }

        if (needsDimensionReset) {
            dispatch(changeDimension(DEFAULT_DIMENSION));
        }

        /**
         * Important:
         * daca am resetat dimension/filterBy, nu facem request in acelasi ciclu.
         * Asteptam urmatorul render, cu valorile corecte din Redux.
         */
        if (needsFilterReset || needsDimensionReset) {
            return;
        }

        initializedPageRef.current = pageKey;

        let active = true;
        setDataReloaded(false);
        setFullReport([]);
        dispatch(getDealsData(
            { period, dimension, filterBy, publisherView, partition: PARTITION },
            (rows) => { if (active) setFullReport(rows); },
            (loaded) => { if (active) setDataReloaded(loaded); }
        ));
        return () => { active = false; };

    }, [dispatch, period, dimension, filterBy, publisherView, PARTITION, DIMENSIONS, DEFAULT_DIMENSION]);

    const chartData = useMemo(() => dataReloaded
        ? chartPreparation(prepareChartRows(fullReport, dimension), dimension, selectedCard, period)
        : { topValues: [], data: [] },
        [fullReport, dimension, selectedCard, period, dataReloaded]);

    if (loading) {
        return <Loader />;
    }

    return (
        <>
            <h2>{`Direct deals for ${GREETINGS_FOR} account`}</h2>
            <FiltersSection
                dimensions={DIMENSIONS}
                defaultSelection={DEFAULT_DIMENSION}
            />
            <div className="main-section">
                <SelectedMainCards
                    partition={PARTITION}
                    reportScope="deals"
                    fullData={fullReport}
                    trigger={setSelectedCard}
                    currentState={selectedCard}
                    limited
                />
                <DynamicChart
                    fullData={chartData}
                    currentState={selectedCard}
                />
            </div>
            {
                !isThdPage 
                    ? <SummaryTable fullData={fullReport}currentState={selectedCard}/>
                    : <DealsSummaryTable fullData={fullReport} currentState={selectedCard} ready={dataReloaded}/>
            }
        </>
    );
};

export default DealsPage;